import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthUserProfile, Club } from '../types';
import { authService } from '../services/authService';
import { clearAuthSession, getAuthItem, isAuthenticated } from '../services/authStorage';
import { clubService } from '../services/clubService';

interface PortalContextValue {
  initialized: boolean;
  loading: boolean;
  profile: AuthUserProfile | null;
  ownedClubs: Club[];
  selectedClubId: string;
  selectedClub: Club | null;
  error: string | null;
  setSelectedClubId: (clubId: string) => void;
  refreshPortal: () => Promise<void>;
  signOut: () => void;
}

const SELECTED_CLUB_STORAGE_KEY = 'clubPortalSelectedClubId';

const PortalContext = createContext<PortalContextValue | undefined>(undefined);

export const PortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<AuthUserProfile | null>(null);
  const [ownedClubs, setOwnedClubs] = useState<Club[]>([]);
  const [selectedClubId, setSelectedClubIdState] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setSelectedClubId = useCallback((clubId: string) => {
    setSelectedClubIdState(clubId);
    try {
      localStorage.setItem(SELECTED_CLUB_STORAGE_KEY, clubId);
    } catch {
      // Ignore storage failures.
    }
  }, []);

  const signOut = useCallback(() => {
    clearAuthSession();
    setProfile(null);
    setOwnedClubs([]);
    setSelectedClubIdState('');
    setError(null);
  }, []);

  const refreshPortal = useCallback(async () => {
    if (!isAuthenticated()) {
      setInitialized(true);
      setLoading(false);
      setProfile(null);
      setOwnedClubs([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextProfile = await authService.getProfile();
      const nextClubs = await clubService.listOwnedClubs(nextProfile.id || getAuthItem('userId'));
      setProfile(nextProfile);
      setOwnedClubs(nextClubs);

      const rememberedClubId = (() => {
        try {
          return localStorage.getItem(SELECTED_CLUB_STORAGE_KEY) || '';
        } catch {
          return '';
        }
      })();

      const preferredClubId =
        nextClubs.find((club) => club.id === selectedClubId)?.id ||
        nextClubs.find((club) => club.id === rememberedClubId)?.id ||
        nextClubs[0]?.id ||
        '';

      setSelectedClubIdState(preferredClubId);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar sessao do portal');
      if (nextError.status === 401) {
        signOut();
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [selectedClubId, signOut]);

  useEffect(() => {
    refreshPortal();
  }, [refreshPortal]);

  const value = useMemo<PortalContextValue>(
    () => ({
      initialized,
      loading,
      profile,
      ownedClubs,
      selectedClubId,
      selectedClub: ownedClubs.find((club) => club.id === selectedClubId) || null,
      error,
      setSelectedClubId,
      refreshPortal,
      signOut,
    }),
    [initialized, loading, profile, ownedClubs, selectedClubId, error, setSelectedClubId, refreshPortal, signOut]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

export const usePortal = (): PortalContextValue => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal deve ser usado dentro de PortalProvider');
  }
  return context;
};